import { useCallback, useEffect, useRef, useState } from "react";

import { Language } from "@/components/language/supported-languages";
import { Spinner } from "@/components/ui/spinner";
import { usePocketBase } from "@/context/pocketbase-context";
import { useTextTranslation } from "@/hooks/api/textTranslate";

interface TextTranslateTooltipProps {
  sourceLanguage: Language;
  targetLanguage: Language;
}

const TextTranslateTooltip = ({
  sourceLanguage,
  targetLanguage,
}: TextTranslateTooltipProps) => {
  const { token, session } = usePocketBase();
  const { translateText, isTranslationLoading } = useTextTranslation();
  const [sourceLang, setSourceLang] = useState(sourceLanguage);
  const [targetLang, setTargetLang] = useState(targetLanguage);

  const [tooltipData, setTooltipData] = useState("");
  const [loading, setLoading] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setSourceLang(sourceLanguage);
  }, [sourceLanguage]);

  useEffect(() => {
    setTargetLang(targetLanguage);
  }, [targetLanguage]);

  // Function to fetch data from API
  const fetchTooltipData = useCallback(
    async (text: string) => {
      if (!text.trim()) return;
      console.log("Fetching tooltip data for:", text);
      console.log("Translating from:", sourceLanguage.name);
      console.log("Translating to:", targetLanguage.name);
      const translation = await translateText(
        session,
        token,
        text,
        sourceLang,
        targetLang,
        false,
        1,
      );

      setTooltipData(translation[0].text);
    },
    [session, token, sourceLang, targetLang],
  );

  useEffect(() => {
    setLoading(isTranslationLoading);
  }, [isTranslationLoading]);

  // Handle text selection
  const handleTextSelection = useCallback(() => {
    const selection = window.getSelection();
    const text = selection?.toString().trim();

    if (text) {
      // Calculate position for tooltip
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();

        setTooltipPosition({
          top: rect.top - 10, // Position above the selected text
          left: rect.left + rect.width / 2, // Center the tooltip
        });

        setShowTooltip(true);
        void fetchTooltipData(text);
      }
    } else {
      setShowTooltip(false);
      setTooltipData("");
    }
  }, [fetchTooltipData]);

  // Close tooltip when clicking outside
  const handleClickOutside = useCallback((event: Event) => {
    if (
      tooltipRef.current &&
      !tooltipRef.current.contains(event.target as Node) &&
      contentRef.current &&
      contentRef.current.contains(event.target as Node)
    ) {
      setShowTooltip(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mouseup", handleTextSelection);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mouseup", handleTextSelection);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleTextSelection, handleClickOutside]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowTooltip(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <div ref={contentRef as React.RefObject<HTMLDivElement>}>
      {showTooltip && (
        <div
          ref={tooltipRef as React.RefObject<HTMLDivElement>}
          className="absolute bg-teal-600 rounded-lg px-2 py-1.5 z-50 transform -translate-x-1/2 text-primary-foreground"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            transform: "translate(-50%, -100%)",
          }}
        >
          <div className="text-sm m-1">
            {loading ? (
              <Spinner className="m-0 size-4" />
            ) : (
              <div>{tooltipData}</div>
            )}
          </div>
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-teal-600"></div>
        </div>
      )}
    </div>
  );
};

export default TextTranslateTooltip;
